/**
 * VG Size Chart — tab switching, the unit switcher and deep links.
 *
 * Everything here is an upgrade on markup that already works: panels are plain
 * blocks hidden with the `hidden` attribute, the unit columns are filtered by
 * CSS generated in the section, and a <noscript> block in the section reveals
 * both if this never runs.
 */
class VgSizeChart extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.tabs = Array.from(this.querySelectorAll('.vg-chart__tab'));
    this.panels = Array.from(this.querySelectorAll('.vg-chart__panel'));
    this.unitButtons = Array.from(this.querySelectorAll('[data-vg-unit]'));
    this.tabTrack = this.querySelector('.vg-chart__tabs-track');

    this.storageKey = this.dataset.storageKey || 'vg-size-unit';
    this.remembersUnit = this.hasAttribute('data-remember-unit');
    this.deepLinks = this.hasAttribute('data-deep-link');

    // Bound as an arrow so the hashchange Event object is never mistaken for
    // the isInitial flag — a later hash change should scroll, the first load
    // should not.
    this.boundHashChange = () => this.openFromHash(false);

    this.addListeners();
    this.restoreUnit();
    this.watchTabOverflow();
    this.openFromHash(true);
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.boundHashChange);
    if (this.trackObserver) this.trackObserver.disconnect();
  }

  addListeners() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab));
      tab.addEventListener('keydown', this.handleTabKeydown.bind(this));
    });

    this.unitButtons.forEach((button) => {
      button.addEventListener('click', () => this.setUnit(button.dataset.vgUnit));
    });

    if (this.deepLinks) {
      window.addEventListener('hashchange', this.boundHashChange);
    }

    if (this.tabTrack) {
      this.tabTrack.addEventListener('scroll', this.updateTrackFades.bind(this), { passive: true });
    }
  }

  /* Tabs ------------------------------------------------------------------ */

  selectTab(tab, { focus = false, updateHash = true } = {}) {
    if (!tab || tab.getAttribute('aria-selected') === 'true') return;

    const panelId = tab.getAttribute('aria-controls');

    this.tabs.forEach((other) => {
      const isCurrent = other === tab;
      other.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
      other.setAttribute('tabindex', isCurrent ? '0' : '-1');
      other.classList.toggle('is-active', isCurrent);
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });

    this.scrollTabIntoView(tab);
    if (focus) tab.focus();

    if (updateHash && this.deepLinks && tab.dataset.handle) {
      // replaceState keeps the shopper's Back button pointing at the previous
      // page rather than at every tab they clicked through.
      const url = `${window.location.pathname}${window.location.search}#${tab.dataset.handle}`;
      window.history.replaceState(null, '', url);
    }

    this.dispatchEvent(
      new CustomEvent('vg-size-chart:tab', {
        bubbles: true,
        detail: { handle: tab.dataset.handle, panelId }
      })
    );
  }

  handleTabKeydown(evt) {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(evt.key)) return;

    evt.preventDefault();
    const current = this.tabs.indexOf(evt.currentTarget);
    let next = current;

    switch (evt.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        break;
    }

    this.selectTab(this.tabs[next], { focus: true });
  }

  scrollTabIntoView(tab) {
    if (!this.tabTrack || this.tabTrack.scrollWidth <= this.tabTrack.clientWidth) return;

    const trackBox = this.tabTrack.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();

    if (tabBox.left < trackBox.left || tabBox.right > trackBox.right) {
      this.tabTrack.scrollTo({
        left: this.tabTrack.scrollLeft + (tabBox.left - trackBox.left) - 24,
        behavior: 'smooth'
      });
    }
  }

  /* The fades tell the shopper there are more tabs off-screen — without them a
     narrow phone silently hides half the brands. */
  watchTabOverflow() {
    if (!this.tabTrack) return;

    this.updateTrackFades();

    if ('ResizeObserver' in window) {
      this.trackObserver = new ResizeObserver(this.updateTrackFades.bind(this));
      this.trackObserver.observe(this.tabTrack);
    }
  }

  updateTrackFades() {
    if (!this.tabTrack) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.tabTrack;
    const wrapper = this.tabTrack.parentElement;

    wrapper.classList.toggle('is-scrolled-start', scrollLeft > 2);
    wrapper.classList.toggle('is-scrolled-end', scrollLeft + clientWidth < scrollWidth - 2);
  }

  /* Units ----------------------------------------------------------------- */

  setUnit(unit, { persist = true } = {}) {
    if (!unit || !this.unitButtons.length) return;

    const match = this.unitButtons.find((button) => button.dataset.vgUnit === unit);
    if (!match) return;

    this.dataset.unit = unit;
    this.unitButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button === match ? 'true' : 'false');
    });

    if (persist && this.remembersUnit) {
      // Private browsing and blocked storage both throw here; the switcher
      // still works for the session, it just won't be remembered.
      try {
        window.localStorage.setItem(this.storageKey, unit);
      } catch (error) {
        this.remembersUnit = false;
      }
    }

    this.dispatchEvent(
      new CustomEvent('vg-size-chart:unit', { bubbles: true, detail: { unit } })
    );
  }

  restoreUnit() {
    if (!this.remembersUnit || !this.unitButtons.length) return;

    let stored = null;
    try {
      stored = window.localStorage.getItem(this.storageKey);
    } catch (error) {
      this.remembersUnit = false;
      return;
    }

    if (stored) this.setUnit(stored, { persist: false });
  }

  /* Deep links ------------------------------------------------------------ */

  openFromHash(isInitial = false) {
    if (!this.deepLinks || !this.tabs.length) return;

    const handle = window.location.hash.replace('#', '');
    if (!handle) return;

    const tab = this.tabs.find((candidate) => candidate.dataset.handle === handle);
    if (!tab) return;

    this.selectTab(tab, { updateHash: false });

    if (!isInitial) {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

customElements.define('vg-size-chart', VgSizeChart);
